// src/pages/AdminResourcesPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';

export default function AdminResourcesPage() {
  const { user } = useUser();
  const [selectedDepartment, setSelectedDepartment] = useState('ALL');
  const [selectedBranch, setSelectedBranch] = useState('NCCU');
  const [resources, setResources] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    link: '',
    department: 'ALL',
    branch: 'ALL',
    category: 'material'
  });

  // 권한 확인 - STAFF 이상 접근 가능
  if (!user?.isLoggedIn || !['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }


  useEffect(() => {
    fetchResources();
  }, [selectedDepartment, selectedBranch]);

  const fetchResources = async () => {
    try {
      const response = await axios.get('/api/admin/resources', {
        params: {
          department: selectedDepartment,
          branch: selectedBranch
        }
      });
      setResources(response.data);
    } catch (error) {
      setResources([]);
    }
  };

  // 백엔드에서 필터링된 데이터를 받아오므로 추가 필터링 불필요
  const filteredResources = resources;

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'material': return '📁';
      case 'rule': return '📋';
      case 'tool': return '🛠️';
      default: return '📄';
    }
  };

  const getDepartmentColor = (department) => {
    switch (department) {
      case 'ALL': return 'bg-gray-100 text-gray-800';
      case 'GA': return 'bg-blue-100 text-blue-800';
      case 'PR': return 'bg-purple-100 text-purple-800';
      case 'EP': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    
    if (!newResource.title || !newResource.description || newResource.description.trim() === '') {
      alert('Please fill in title and description.');
      return;
    }

    try {
      const response = await axios.post('/api/admin/resources', newResource);
      
      setResources(prev => [response.data, ...prev]);
      setNewResource({
        title: '',
        description: '',
        link: '',
        department: 'ALL',
        branch: 'ALL',
        category: 'material'
      });
      setShowCreateModal(false);
      alert('Resource created successfully!');
    } catch (error) {
      alert('Failed to create resource: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteResource = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/resources/${id}`);
      setResources(prev => prev.filter(resource => resource.id !== id));
      alert('Resource deleted successfully!');
    } catch (error) {
      alert('Failed to delete resource: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
    setShowDetailModal(true);
  };


  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <h1 className="text-4xl font-bold text-gray-800">Materials & Rules</h1>
        {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
          <button 
            onClick={() => setShowCreateModal(true)} 
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            + Create New Resource
          </button>
        )}
      </div>

      {/* Materials & Rules Section - Urgent Tasks 스타일 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">📚 Resources & Materials</h2>
        
        {/* Branch Filter - 클릭 가능한 박스들 */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-medium text-blue-900 mb-3">Select Branch</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={() => setSelectedBranch('NCCU')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedBranch === 'NCCU'
                  ? 'border-blue-500 bg-blue-100 text-blue-900'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <div className="font-medium">NCCU</div>
              <div className="text-sm text-gray-600">NCCU resources</div>
            </button>
            <button
              onClick={() => setSelectedBranch('NTU')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedBranch === 'NTU'
                  ? 'border-blue-500 bg-blue-100 text-blue-900'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <div className="font-medium">NTU</div>
              <div className="text-sm text-gray-600">NTU resources</div>
            </button>
            <button
              onClick={() => setSelectedBranch('TAIPEI')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedBranch === 'TAIPEI'
                  ? 'border-blue-500 bg-blue-100 text-blue-900'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <div className="font-medium">TAIPEI</div>
              <div className="text-sm text-gray-600">TAIPEI resources</div>
            </button>
          </div>
        </div>

        {/* Department Filter */}
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="text-lg font-medium text-green-900 mb-3">Filter by Department</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <button
              onClick={() => setSelectedDepartment('ALL')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedDepartment === 'ALL'
                  ? 'border-green-500 bg-green-100 text-green-900'
                  : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
              }`}
            >
              <div className="font-medium">All Departments</div>
              <div className="text-sm text-gray-600">Show all teams</div>
            </button>
            <button
              onClick={() => setSelectedDepartment('GA')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedDepartment === 'GA'
                  ? 'border-green-500 bg-green-100 text-green-900'
                  : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
              }`}
            >
              <div className="font-medium">GA</div>
              <div className="text-sm text-gray-600">General Affairs</div>
            </button>
            <button
              onClick={() => setSelectedDepartment('PR')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedDepartment === 'PR'
                  ? 'border-green-500 bg-green-100 text-green-900'
                  : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
              }`}
            >
              <div className="font-medium">PR</div>
              <div className="text-sm text-gray-600">Public Relations</div>
            </button>
            <button
              onClick={() => setSelectedDepartment('EP')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedDepartment === 'EP'
                  ? 'border-green-500 bg-green-100 text-green-900'
                  : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
              }`}
            >
              <div className="font-medium">EP</div>
              <div className="text-sm text-gray-600">Event Planning</div>
            </button>
          </div>
        </div>
        
        {/* 현재 필터 상태 표시 */}
        <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
        <div>
              <span className="text-sm text-gray-700">
                Showing: <span className="font-medium">{selectedDepartment}</span> • <span className="font-medium">{selectedBranch}</span> ({filteredResources.length} resources)
              </span>
            </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredResources.map(resource => (
                     <div 
             key={resource.id} 
              className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border border-gray-200 cursor-pointer"
             onClick={() => handleResourceClick(resource)}
           >
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getCategoryIcon(resource.category)}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDepartmentColor(resource.department)}`}>
                  {resource.department}
                </span>
                  {resource.branch !== 'ALL' && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {resource.branch}
                    </span>
                  )}
              </div>
              <div className="flex gap-2">
                {(user?.role === 'LEADER' || user?.role === 'PRESIDENT') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Edit functionality would go here
                      alert('Edit functionality coming soon');
                    }}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    ✏️
                  </button>
                )}
                {(user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteResource(resource.id);
                    }}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
            
                         <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{resource.title}</h2>
              <div className="text-gray-600 mb-4 text-sm">
               <div 
                 className="prose prose-sm max-w-none" 
                 dangerouslySetInnerHTML={{ __html: resource.description }} 
               />
             </div>
            
            <div className="flex justify-between items-center">
              {resource.link && (
                <a 
                  href={resource.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  🔗 Open Link
                </a>
              )}
              </div>
            </div>
          ))}
          </div>
      </div>

             {/* Create Resource Modal */}
       {showCreateModal && (
         <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
           <div className="bg-white p-6 sm:p-8 rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Create New Resource</h2>
            
            <form onSubmit={handleCreateResource} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newResource.title}
                  onChange={(e) => setNewResource(prev => ({...prev, title: e.target.value}))}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

    <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={newResource.description}
                  onChange={(e) => setNewResource(prev => ({...prev, description: e.target.value}))}
                  className="w-full p-3 border rounded-md min-h-[120px] resize-vertical"
                  placeholder="Enter resource description..."
                  required
                 />
               </div>

              <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-1">Link (Optional)</label>
                 <input
                   type="url"
                   value={newResource.link}
                   onChange={(e) => setNewResource(prev => ({...prev, link: e.target.value}))}
                   className="w-full p-2 border rounded-md"
                   placeholder="https://... (optional)"
                 />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  value={newResource.department}
                  onChange={(e) => setNewResource(prev => ({...prev, department: e.target.value}))}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="ALL">All Departments</option>
                  <option value="GA">GA (General Affairs)</option>
                  <option value="PR">PR (Public Relations)</option>
                  <option value="EP">EP (Event Planning)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={newResource.branch}
                  onChange={(e) => setNewResource(prev => ({...prev, branch: e.target.value}))}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="ALL">All Branches</option>
                  <option value="NCCU">NCCU</option>
                  <option value="NTU">NTU</option>
                  <option value="TAIPEI">TAIPEI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newResource.category}
                  onChange={(e) => setNewResource(prev => ({...prev, category: e.target.value}))}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="material">📁 Material</option>
                  <option value="rule">📋 Rule</option>
                  <option value="tool">🛠️ Tool</option>
                </select>
              </div>

                             <div className="flex gap-3 mt-6">
                 <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                   Create
                 </button>
                 <button 
                   type="button" 
                   onClick={() => setShowCreateModal(false)} 
                   className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                 >
                   Cancel
                 </button>
               </div>
            </form>
          </div>
        </div>
      )}

             {/* Detail Modal */}
       {showDetailModal && selectedResource && (
         <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
           <div className="bg-white p-4 sm:p-8 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                         <div className="flex justify-between items-start mb-6">
               <div className="flex items-center gap-2 sm:gap-3">
                 <span className="text-3xl sm:text-4xl">{getCategoryIcon(selectedResource.category)}</span>
                 <div>
                   <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{selectedResource.title}</h2>
                   <div className="flex gap-2 mt-2">
                   <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getDepartmentColor(selectedResource.department)}`}>
                     {selectedResource.department}
                   </span>
                     {selectedResource.branch !== 'ALL' && (
                       <span className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800">
                         {selectedResource.branch}
                       </span>
                     )}
                   </div>
                 </div>
               </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Description</h3>
                                 <div className="bg-gray-50 p-4 rounded-lg">
                   <div 
                     className="prose max-w-none text-gray-700" 
                     dangerouslySetInnerHTML={{ __html: selectedResource.description }} 
                   />
                 </div>
              </div>
              
              {selectedResource.link && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Link</h3>
                  <a 
                    href={selectedResource.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    🔗 {selectedResource.link}
                  </a>
                </div>
              )}
              
              <div className="flex justify-between items-center pt-4 border-t">
                {(user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                  <button
                    onClick={() => handleDeleteResource(selectedResource.id)}
                    className="text-red-500 hover:text-red-700 font-medium"
                  >
                    🗑️ Delete Resource
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
